function __match(v,ex){if(ex===null)return v===null;if(ex===undefined)return true;const t=typeof ex;if(t==='string'||t==='number'||t==='boolean')return typeof v===t;if(Array.isArray(ex)){if(!Array.isArray(v))return false;return ex.length?v.every(x=>__match(x,ex[0])):true}if(t==='object'){if(!v||typeof v!=='object'||Array.isArray(v))return false;return Object.keys(ex).every(k=>k in v&&__match(v[k],ex[k]))}return v===ex};
function Type(d,p,e){const t={description:d,__runtimeType:true};if(typeof p==='function'){t.check=p;t.default=e??null}else{const ex=e??p;t.default=ex;t.__ex=ex;t.check=v=>__match(v,ex)}return t};
const __tjs = globalThis.__tjs?.createRuntime?.() ?? {Type};
/* tjs <- component.ts */

/*#
# Web-Components

**tosijs** provides the abstract `Component` class to make defining custom-elements
easier.

- `Component` leverages the [elements](/elements/) proxy and [css](/css/) to make
defining elements very efficient.
- `Component` makes it easy to create custom-elements with
no shadowDOM but with slotting behavior so your elements are lighter weight and
easier to style.
- It solves friction points like allowing element tagNames to be changed on-the-fly to avoid registry clashes.
- It allows you to deploy `Component` classes as zero dependency [blueprints](/blueprint-loader/) functions.

## Component

To define a custom-element you can subclass `Component`, simply add the properties
and methods you want, with some help from `Component` itself, and then simply
export your new class's `elementCreator()` which is a function that defines your
new component's element and produces instances of it as needed.

```
import {Component} from 'tosijs'

class ToolBar extends Component {
  static preferredTagName = 'tool-bar'
  static shadowStyleSpec = {
    ':host': {
      display: 'flex',
      gap: '10px',
    },
  }
}

export const toolBar = ToolBar.elementCreator()
```

> **Note**: Custom elements default to `display: inline`, which often causes them to
> appear dimensionless. Unless you want this (e.g., for content-holder elements),
> set an explicit `display` value (e.g., `block`, `inline-block`, `flex`) in your
> `:host` styles.

This component is just a structural element. By default a `Component` subclass will
comprise itself and a `<slot>`. You can change this by giving your subclass its
own `content` template.

`static preferredTagName` sets the desired tag name for the custom element.
If omitted, it is derived from the class name (e.g. `ToolBar` → `tool-bar`),
but this does not survive minification. `elementCreator()` returns an
`ElementCreator` function that creates instances of the element.

See [elements](/elements/) for more information on `ElementCreator` functions.

### Component properties

#### content: Element | Element[] | () => Element | () => Element[] | null

Here's a simple example of a custom-element that simply produces a
`<label>` wrapped around `<span>` and an `<input>`. Its value is synced
to that of its `<input>` so the user doesn't need to care about how
it works internally.

```js
import { Component, elements } from 'tosijs'

class LabeledInput extends Component {
  static initAttributes = { caption: 'untitled' }
  value = ''

  content = ({label, span, input}) => label(span(), input())

  connectedCallback() {
    super.connectedCallback()
    const {input} = this.parts
    input.addEventListener('input', () => {
      this.value = input.value
    })
  }

  render() {
    super.render()
    const {span, input} = this.parts
    span.textContent = this.caption
    if (input.value !== this.value) {
      input.value = this.value
    }
  }
}

const labeledInput = LabeledInput.elementCreator()

preview.append(
  labeledInput({caption: 'A text field', value: 'some text'})
)
```

`content` is, in essence, a template for the internals of the element. By default
it's a single `<slot>` element. If you explicitly want an element with no content
you can set your subclass's content to `null` or omit any `<slot>` from its template.

By setting content to be a function that returns elements instead of a collection
of elements you can take customize elements based on the component's properties.
In particular, you can use `onXxxx` syntax sugar to bind events.

(Note that **data bindings do not operate inside a shadowDOM** — binding dispatch
cannot "see" elements there, and a component that tries now gets a console warning
instead of silent failure. The semantically correct model: **a component with a
shadowDOM is bound like an `<input>` or `<textarea>` — its `value` is the binding
surface.** Bind the component itself (e.g. `bindings.value`) from outside; setting
`value` automatically queues `render()` and emits `change`, so implement `render()`
to reflect `value` into the shadow DOM and let `change` events carry edits back out.
How the component represents its value internally is the implementer's business —
which also means shadow components don't compose *bindings* internally: wiring
nested widgets inside a shadow tree is manual (set their `value` in `render()`,
listen to their events). A shadowDOM component is materially a different thing than
a lightDOM component. For non-value internal state, `observe()` + `parts`, with
`unobserve()` on disconnect. **Event sugar is the exception**: `on()` handlers work
inside open shadow roots — composed events cross the boundary and the dispatcher
resolves the true origin via `composedPath()`.)

##### ElementProps in content arrays

When `content` returns an array, any plain objects (ElementProps) in the array are
applied to the **host element** itself, just as they would be applied to the element
being created by `div()`, `span()`, etc. This provides a clean way to set up styles,
event handlers, classes, and bindings on the component from within `content`:

```
class MyButton extends Component {
  static preferredTagName = 'my-button'

  content = ({span}) => [
    { onClick: () => console.log('clicked!'), style: { cursor: 'pointer' } },
    span({part: 'label'}, 'Click me'),
  ]
}
```

Multiple ElementProps objects are merged (later values override earlier ones).
Only plain objects are treated as props — DOM nodes, strings, numbers, and proxied
values pass through as children.

If you'd like to see a more complex example along the same lines, look at
[form and field](https://ui.tosijs.net/form/).

##### <slot> names and the `slot` attribute

```
class MenuBar extends Component {
  static shadowStyleSpec = {
    ':host, :host > slot': {
      display: 'flex',
    },
    ':host > slot:nth-child(1)': {
      flex: '1 1 auto'
    },
  }

  content = ({slot}) => [slot(), slot({name: 'gadgets'})]
}

export menuBar = MenuBar.elementCreator()
```

One of the neat things about custom-elements is that you can give them *multiple*
`<slot>`s with different `name` attributes and then have children target a specific
slot using the `slot` attribute.

This app's layout (the nav sidebar that disappears if the app is in a narrow space, etc.)
is built using just such a custom-element.

#### `<tosi-slot>`

If you put `<slot>` elements inside a `Component` subclass that doesn't have a
shadowDOM, they will automatically be replaced with `<tosi-slot>` elements that
have the expected behavior (i.e. sucking in children in based on their `<slot>`
attribute).

`<tosi-slot>` doesn't support `:slotted` but since there's no shadowDOM, just
style such elements normally, or use `tosi-slot` as a CSS-selector.

Note that you cannot give a `<slot>` element attributes (other than `name`) so if
you want to give a `<tosi-slot>` attributes (such as `class` or `style`), create it
explicitly (e.g. using `elements.tosiSlot()`) rather than using `<slot>` elements
and letting them be switched out (because they'll lose any attributes you give them).

> The legacy name `<xin-slot>` still works but emits a deprecation warning.

Here's a very simple example:

```js
import { Component, elements } from 'tosijs'

class FauxSlotExample extends Component {
  content = ({h4, h5, tosiSlot}) => [
    h4('This is a web-component with no shadow DOM and working slots!'),
    h5('top slot'),
    tosiSlot({name: 'top'}),
    h5('middle slot'),
    tosiSlot(),
    h5('bottom slot'),
    tosiSlot({name: 'bottom'}),
  ]
}

FauxSlotExample.preferredTagName = 'faux-slot-example'
FauxSlotExample.lightStyleSpec = {
  ':host': {
    display: 'flex',
    flexDirection: 'column'
  },
  ':host h4, :host h5': {
    margin: 0,
  },
  ':host tosi-slot': {
    border: '2px solid grey'
  }
}
const fauxSlotExample = FauxSlotExample.elementCreator()

const { div } = elements

preview.append(
  fauxSlotExample(
    div({slot: 'bottom'}, 'I should be on the bottom'),
    div({slot: 'top'}, 'I should be on the top'),
    div('I should be in the middle')
  )
)
```

> ##### Background
>
> `<slot>` elements do not work as expected in shadowDOM-less components. This is
> hugely annoying since it prevents components from composing nicely unless they
> have a shadowDOM, and while the shadowDOM is great for small widgets, it's
> terrible for composite views and excludes `tosijs`'s data bindings (inside the
> shadow DOM you manage state updates yourself with `observe()` + `parts`;
> `on()` event handlers do work there via `composedPath()`).

#### styleNode: HTMLStyleElement

`styleNode` is the `<style>` element that will be inserted into the element's
`shadowRoot`.

If a `Component` subclass has no `styleNode`, no `shadowRoot` will be
created. This reduces the memory and performance cost of the element.

This is to avoid the performance/memory costs associated with the `shadowDOM`
for custom-elements with no styling.

##### Notes

Styling custom-elements can be tricky, and it's worth learning about
how the `:host` and `:slotted()` selectors work.

It's also very useful to understand how CSS-Variables interact with the
`shadowDOM`. In particular, CSS-variables are passed into the `shadowDOM`
when other CSS rules are not. You can use css rules to modify css-variables
which will then penetrate the `shadowDOM`.

#### refs: {[key:string]: Element | undefined}

    render() {
      super.render() // see note
      const {span, input} = this.parts
      span.textContent = this.caption
      if (input.value !== this.value) {
        input.value = this.value
      }
    }

> **Note**: For form-associated components, `super.render()` syncs the form value
> automatically when the value changes. Always call `super.render()` if you override
> `render()` in a form-associated component.
>
> It is *necessary* to call `super.connectedCallback`, `super.disconnectedCallback`,
> `super.render()` (for form-associated), and `super()` in the `constructor()`
> should you override them.

`this.parts` returns a proxy that provides elements conveniently and efficiently. It
is intended to facilitate access to static elements (it memoizes its values the
first time they are computed).

`this.parts.foo` finds a content element by, in order: `part="foo"` (the preferred
form — it's also what `::part()` styling targets), then `data-ref="foo"`, and
finally `foo` as a css selector — so `this.parts['.foo']` finds a content element
with `class="foo"` while `this.parts.h1` finds an `<h1>`.

`this.parts` will also remove a `data-ref` attribute once it has been used to find
the element. This means that if you use all your refs in `render` or `connectedCallback`
then no trace will remain in the DOM for a mounted element.

`parts` only resolves after **hydration** — the content it looks through is
instantiated on `connectedCallback`, not at construction. Reading `parts` on an
uninserted element (e.g. one just back from `elementCreator()`) has nothing to find.
If a public getter needs a ref before the element is guaranteed inserted, gate it on
`this.hydrated` or `await this.whenHydrated` first. (Prior to this you could not ask
whether an element was hydrated without probing `parts`, and that probe permanently
bound the proxy to the light DOM.)

### Component properties

#### content: ((elements: ElementsProxy) => ContentType) | null | ContentType = slot()

A component's content `property` can either be static content (it defaults to being a `<slot>` element) or an arrow function
that creates the basic content of the element on hydration. Static content will be deep-cloned.

By using an arrow function the content created can refer to the custom-element's properties and attributes (and this occurs post-initialization). This also means you can bind event-handlers in the component (which should also be arrow functions unless they don't need to refer to the element)

Because a `content` function is passed the `elements` proxy, you can easily destructure any element creators you need:

```
content = ({div}) => div('hello world')
```

`ContentType` can be an HTMLElement or an array of elements.

> Note that if a component does not use the shadowDOM, its `<slot>` elements will be replaced with `<tosi-slot>` elements.
> This allows composition to work as expected without requiring the shadow DOM.

### Component static properties

#### static initAttributes: Record<string, any>

Declares attributes that should be watched and synced with properties. The keys are
property names (camelCase), and the values are the defaults which also determine the type.

    import { Component } from 'tosijs'

    class MyWidget extends Component {
      static initAttributes = {
        caption: '',      // string attribute
        count: 0,         // number attribute (auto-parsed)
        disabled: false,  // boolean attribute (presence/absence)
      }

      render() {
        // this.caption, this.count, this.disabled are automatically available
        // and synced with HTML attributes
      }
    }

This replaces both the old `initAttributes()` method call AND the instance property
declarations. A single static object now defines which properties are attributes,
their default values, and their types:

- **All-in-one**: Attributes, defaults, and types defined in one place
- **Declarative**: No constructor needed
- **Type inference**: Default values determine parsing (boolean attributes just work)

##### Attribute Types

- **string** (default `''`): Attribute value used as-is
- **number** (default `0`): Attribute value parsed with `parseFloat()`
- **boolean** (default `false`): Presence means `true`, absence means `false`

For non-attribute properties (e.g. objects), just declare them as regular instance
properties on your class.

##### Migration from initAttributes()

Old (deprecated):

    class MyComponent extends Component {
      caption = ''
      count = 0

      constructor() {
        super()
        this.initAttributes('caption', 'count')
      }
    }

New:

    class MyComponent extends Component {
      static initAttributes = { caption: '', count: 0 }
    }

### Component methods

#### queueRender(triggerChangeEvent = false): void

Uses `requestAnimationFrame` to queue a call to the component's `render` method. If
called with `true` it will also trigger a `change` event.

#### private initValue(): void

**Don't call this!** Sets up expected behavior for an `HTMLElement` with
a value (i.e. triggering a `change` events and `render` when the `value` changes).

#### private hydrate(): void

**Don't call this** Appends `content` to the element (or its `shadowRoot` if it has a `styleNode`)

#### connectedCallback(): void

If the class has a `handleResize` handler then a ResizeObserver will trigger
`resize` events on the element when its size changes and `handleResize` will be
set up to respond to them. (The legacy name `onResize` still works but is
deprecated — the `on<Event>` prefix is reserved for event-handler sugar in the
[elements](/elements/) factory, so component members must not use it.) Name by
intent: `handle<Event>` for a handler function the component invokes (e.g.
`handleResize`, `handleClick`), or `add<Event>Listener` for a method that
registers listeners for a synthetic event the component dispatches (e.g.
`addClickListener`).

Also, if the subclass has defined `value`, calls `initValue()`.

`connectedCallback` is a great place to attach **event-handlers** to elements in your component.

Be sure to call `super.connectedCallback()` if you implement `connectedCallback` in the subclass.

#### disconnectedCallback(): void

Be sure to call `super.disconnectedCallback()` if you implement `disconnectedCallback` in the subclass.

#### render(): void

Be sure to call `super.render()` if you implement `render` in the subclass.

### Component static properties

#### Component.elements

    const {label, span, input} = Component.elements

This is simply provided as a convenient way to get to [elements](/elements/)

#### static formAssociated: boolean

Set `static formAssociated = true` in your subclass to enable form participation
via `ElementInternals`. When true, the component will have `this.internals` available
for form integration, validation, ARIA properties, and custom states.

Form-associated components are automatically made focusable (`tabindex="0"`) unless
you explicitly set a different `tabindex`. This is required for form validation to
work correctly (the browser needs to focus invalid elements).

See [web-component-validation](/form-validation/) for the complete validation
API documentation, including:

- Validation methods (`checkValidity()`, `reportValidity()`, `setValidity()`)
- Automatic validation against HTML attributes (`required`, `minlength`, `maxlength`, `pattern`)
- Form lifecycle callbacks (`formResetCallback`, `formDisabledCallback`, `formStateRestoreCallback`)
- Custom states via `this.internals.states`
- Complete examples

#### value property

**If your component has a `value`, it should behave like an `<input>`.**

The `value` property is special in Component. It is NOT an attribute - it's a property
that can be *initialized* from an attribute. Here's what you need to know:

1. **Declare it with a default**: Simply assign a non-undefined default (e.g., `value = ''`)
2. **Initialization**: If a `value` attribute is present, it initializes the property (as a string)
3. **Setting value**: You can set it to any type directly (e.g., objects, arrays)
4. **Change events**: When `value` changes, a `change` event is automatically dispatched
5. **Auto-render**: When `value` changes, `render()` is automatically called
6. **Computed values**: If your value is computed, call `queueRender(true)` to trigger change + render

**Do NOT put `value` in `static initAttributes`** - it will be rejected with a warning.
The Component class handles `value` specially to provide form-like behavior automatically.

#### adoptedCallback

The `adoptedCallback` lifecycle method is called when a component is moved to a different
document, such as into or out of an iframe. Subclasses can implement this directly.

```js
import { Component, elements } from 'tosijs'

class AdoptableWidget extends Component {
  docCount = 0

  content = ({span}) => span({part: 'info'})

  adoptedCallback() {
    this.docCount++
    this.queueRender()
  }

  render() {
    this.parts.info.textContent = `Adopted ${this.docCount} time(s). Document: ${this.ownerDocument.title || 'untitled'}`
  }
}

AdoptableWidget.preferredTagName = 'adoptable-widget'
const adoptableWidget = AdoptableWidget.elementCreator()
const {iframe, button, div, span} = elements

const widget = adoptableWidget()
const widgetSlot = span({class: 'widget-slot'}, widget)
const frame = iframe()
const moveBtn = button('Move to iframe')
const backBtn = button('Move back')

moveBtn.addEventListener('click', () => {
  frame.contentDocument.body.append(frame.contentDocument.adoptNode(widget))
})
backBtn.addEventListener('click', () => {
  widgetSlot.append(document.adoptNode(widget))
})

preview.append(widgetSlot, div(moveBtn, backBtn), frame)
```
```css
.preview .widget-slot {
  display: block;
  min-height: 40px;
  border: 2px dashed #888;
  margin-bottom: 10px;
}
.preview adoptable-widget {
  display: block;
  padding: 10px;
  background: #666;
  color: white;
}
.preview > div { display: flex; gap: 8px; margin-bottom: 10px; }
.preview iframe {
  width: 100%;
  height: 60px;
  border: 2px dashed #888;
  background: #fff;
}
```

### Component static properties

#### `static preferredTagName?: string`

Sets the desired tag name for the custom element. If omitted, it is derived
from the class name (e.g. `ToolBar` → `tool-bar`), but this does **not** survive
minification. If the tag is already in use, a unique anonymous tag is generated.

#### `static shadowStyleSpec?: XinStyleSheet`

Styles injected into the component's shadow DOM as a `<style>` element.
Setting this property causes the component to use shadow DOM.

#### `static lightStyleSpec?: XinStyleSheet`

Global styles appended to `document.head` when the first instance is inserted
in the DOM. `:host` selectors are automatically rewritten to the tag name, e.g.:

    class ToolBar extends Component {
      static preferredTagName = 'tool-bar'
      static lightStyleSpec = {
        ':host': {
          display: 'flex',
          padding: 'var(--toolbar-padding, 0 8px)',
          gap: '4px'
        }
      }
    }

produces `tool-bar { display: flex; ... }` in a global `<style>` element.

#### `static extends?: string`

For customized built-in elements. Passed as `{ extends }` to `customElements.define()`.

### Component static methods

#### Component.elementCreator(): ElementCreator

    export const toolBar = ToolBar.elementCreator()

Returns a function that creates the custom-element. Registration uses
`preferredTagName`, `lightStyleSpec`, `shadowStyleSpec`, and `extends`
from the class's static properties.

`elementCreator` is memoized and only generated once.

> **Deprecated:** Passing `{ tag, styleSpec, extends }` as options to
> `elementCreator()` still works but emits deprecation warnings.
> Use the static properties instead.

## Examples

[tosijs-ui](https://ui.tosijs.net) is a component library built using this `Component` class
that provides the essential additions to standard HTML elements needed to build many
user-interfaces.

- [live-example](https://ui.tosijs.net/live-example/) uses multiple named slots to implement
  powers the interactive examples used for this site.
- [side-nav](https://ui.tosijs.net/side-nav/) implements the sidebar navigation
  used on this site.
- [data-table](https://ui.tosijs.net/data-table/) implements virtualized tables
  with resizable, reorderable, sortable columns that can handle more data
  than you're probably willing to load.
- [form and field](https://ui.tosijs.net/form/) allow you to
  quickly create forms that leverage all the built-in functionality of `<input>`
  elements (including powerful validation) even for custom-fields.
- [markdown-viewer](https://ui.tosijs.net/markdown-viewer/) uses `marked` to render
  markdown.
- [babylon-3d](https://ui.tosijs.net/babylon-3d/) lets you easily embed 3d scenes
  in your application using [babylonjs](https://babylonjs.com/)
*/

import { css } from './css'

import { XinStyleSheet } from './css-types'

import { deepClone } from './deep-clone'

import { appendContentToElement, dispatch, resizeObserver } from './dom'

import { ElementsProxy } from './elements-types'

import { elements, elementSet } from './elements'

import { tosiPath } from './metadata'

import { validateAgainstConstraints } from './form-validation'

import { camelToKabob, kabobToCamel } from './string-case'

import { ElementCreator, ContentType, PartsMap } from './xin-types'

import { warnDeprecated, BOUND_SELECTOR } from './metadata'

let anonymousElementCount = 0;

/* line 618 */
function anonElementTag() {
    return `custom-elt${(anonymousElementCount++).toString(36)}`;
}
anonElementTag.__tjs = {
  "params": {},
  "returns": {
    "type": {
      "kind": "string"
    }
  },
  "unsafeReturn": true,
  "unsafe": true,
  "source": "component.ts:618"
}

let instanceCount = 0;

const warnedShadowContentBindings = new Set();

const warnedFieldShadowedAttrs = new Set();

const DRAIN_WRAPPED = Symbol('tosiDrainWrapped');

const handlerCollisionChecked = new WeakSet();

let legacyAttributeObserver = null;

/* line 639 */
function getLegacyAttributeObserver() {
    if (legacyAttributeObserver === null) {
        legacyAttributeObserver = new MutationObserver((mutationsList) => {
            const componentsToRender = new Set();
            for (const mutation of mutationsList) {
                if (mutation.type === 'attributes' &&
                    mutation.target instanceof Component) {
                    const component = mutation.target;
                    const attrName = kabobToCamel(mutation.attributeName);
                    if (component._legacyTrackedAttrs?.has(attrName)) {
                        componentsToRender.add(component);
                    }
                }
            }
            for (const component of componentsToRender) {
                component.queueRender(false);
            }
        });
    }
    return legacyAttributeObserver;
}
getLegacyAttributeObserver.__tjs = {
  "params": {},
  "returns": {
    "type": {
      "kind": "object",
      "shape": {}
    }
  },
  "unsafeReturn": true,
  "unsafe": true,
  "source": "component.ts:639"
}

const ElementCreatorOptions = Type('ElementCreatorOptions', undefined, { tag: '', styleSpec: null })

const globalStyleSheets = {};

/* line 672 */
/* TODO: TS types degraded — styleSpec: XinStyleSheet */
function setGlobalStyle(tagName, styleSpec) {
    const existing = globalStyleSheets[tagName];
    const processed = css(styleSpec)
        .replace(/:host\(([^)]+)\)/g, `${tagName}$1`)
        .replace(/:host\b/g, tagName);
    globalStyleSheets[tagName] = existing
        ? existing + '\n' + processed
        : processed;
}
setGlobalStyle.__tjs = {
  "params": {
    "tagName": {
      "type": {
        "kind": "string"
      },
      "required": true,
      "default": null
    },
    "styleSpec": {
      "type": {
        "kind": "any"
      },
      "required": false
    }
  },
  "unsafe": true,
  "source": "component.ts:676"
}

/* line 682 */
function insertGlobalStyles(tagName) {
    if (globalStyleSheets[tagName]) {
        document.head.append(elements.style({ id: tagName + '-component' }, globalStyleSheets[tagName]));
    }
    delete globalStyleSheets[tagName];
}
insertGlobalStyles.__tjs = {
  "params": {
    "tagName": {
      "type": {
        "kind": "string"
      },
      "required": true,
      "default": null
    }
  },
  "unsafe": true,
  "source": "component.ts:682"
}

export class Component extends HTMLElement {
  static elements = elements;
  static _elementCreator
  static initAttributes
  static formAssociated
  static preferredTagName
  static shadowStyleSpec
  static lightStyleSpec
  static extends
  internals
  get validity() {
    return this.internals?.validity;
}
  get validationMessage() {
    return this.internals?.validationMessage ?? '';
}
  get willValidate() {
    return this.internals?.willValidate ?? false;
}
  checkValidity() {
    return this.internals?.checkValidity() ?? true;
}
  reportValidity() {
    return this.internals?.reportValidity() ?? true;
}
  setCustomValidity(message = '') {
    if (this.internals) {
        if (message) {
            this.internals.setValidity({ customError: true }, message);
        }
        else {
            this.internals.setValidity({});
        }
    }
}
  setValidity(flags,message = '' | undefined,anchor = {} | undefined) {
    this.internals?.setValidity(flags, message, anchor);
}
  setFormValue(value, state) {
    this.internals?.setFormValue(value, state);
}
  static get observedAttributes() {
    const initAttrs = this.initAttributes;
    if (initAttrs) {
        return ['hidden', ...Object.keys(initAttrs).map(camelToKabob)];
    }
    return ['hidden'];
}
  instanceId
  styleNode
  static styleSpec
  static styleNode
  content = elements.slot();
  isSlotted
  static _tagName = null;
  static get tagName() {
    return this._tagName;
}
  _legacyTrackedAttrs
  _attrValues
  _valueChanged = false;
  _pendingAttrOps
  static StyleNode(styleSpec) {
    console.warn('StyleNode is deprecated, use static shadowStyleSpec instead');
    return elements.style(css(styleSpec));
}
  static elementCreator(options = {}) {
    const componentClass = this;
    if (!Object.prototype.hasOwnProperty.call(componentClass, '_elementCreator')) {
                                                     
        if (options.tag !== undefined) {
            warnDeprecated('elementCreator-tag', 'Passing tag to elementCreator() is deprecated. Use static preferredTagName instead.');
        }
        if (options.styleSpec !== undefined) {
            warnDeprecated('elementCreator-styleSpec', 'Passing styleSpec to elementCreator() is deprecated. Use static lightStyleSpec instead.');
        }
        if (options.extends !== undefined) {
            warnDeprecated('elementCreator-extends', 'Passing extends to elementCreator() is deprecated. Use static extends instead.');
        }
                                                                                   
        let tagName = options.tag ?? componentClass.preferredTagName;
        if (tagName == null) {
            if (typeof componentClass.name === 'string' &&
                componentClass.name !== '') {
                tagName = camelToKabob(componentClass.name);
                if (tagName.startsWith('-')) {
                    tagName = tagName.slice(1);
                }
            }
            else {
                tagName = anonElementTag();
            }
        }
        if (customElements.get(tagName) != null) {
            console.warn(`${tagName} is already defined`);
        }
        if (tagName.match(/\w+(-\w+)+/) == null) {
            console.warn(`${tagName} is not a legal tag for a custom-element`);
            tagName = anonElementTag();
        }
        while (customElements.get(tagName) !== undefined) {
            tagName = anonElementTag();
        }
        componentClass._tagName = tagName;
                                                                              
        const lightStyleSpec = options.styleSpec ?? componentClass.lightStyleSpec;
        if (lightStyleSpec !== undefined) {
            setGlobalStyle(tagName, lightStyleSpec);
        }
                                                            
        const extendsTag = options.extends ?? componentClass.extends;
        const defineOptions = extendsTag
            ? { extends: extendsTag }
            : undefined;

        const proto = this.prototype;
        if (proto != null &&
            !Object.prototype.hasOwnProperty.call(proto, DRAIN_WRAPPED)) {
            const inner = proto.connectedCallback;
            proto.connectedCallback = function () {
                const self = this;
                self._drainPendingAttrOps();
                if (inner)
                    inner.call(this);
            };
            Object.defineProperty(proto, DRAIN_WRAPPED, {
                value: true,
                enumerable: false,
            });
        }
        window.customElements.define(tagName, this, defineOptions);
        componentClass._elementCreator = elements[tagName];
    }
    return componentClass._elementCreator;
}
  initAttributes(...attributeNames) {
    warnDeprecated('initAttributes', 'initAttributes() is deprecated. Use static initAttributes = { ... } instead.');
                                                                            
    if (!this._legacyTrackedAttrs) {
        this._legacyTrackedAttrs = new Set();
    }
    for (const name of attributeNames) {
        this._legacyTrackedAttrs.add(name);
    }
                                                                   
    const observer = getLegacyAttributeObserver();
    observer.observe(this, { attributes: true });
    const attributes = {};
    const attributeValues = {};
    attributeNames.forEach((attributeName) => {
        attributes[attributeName] = deepClone(this[attributeName]);
        const attributeKabob = camelToKabob(attributeName);
        Object.defineProperty(this, attributeName, {
            enumerable: false,
            get() {
                if (typeof attributes[attributeName] === 'boolean') {
                    return this.hasAttribute(attributeKabob);
                }
                else {
                    if (this.hasAttribute(attributeKabob)) {
                        return typeof attributes[attributeName] === 'number'
                            ? parseFloat(this.getAttribute(attributeKabob))
                            : this.getAttribute(attributeKabob);
                    }
                    else if (attributeValues[attributeName] !== undefined) {
                        return attributeValues[attributeName];
                    }
                    else {
                        return attributes[attributeName];
                    }
                }
            },
            set(value) {
                if (typeof attributes[attributeName] === 'boolean') {
                    if (value !== this[attributeName]) {
                        if (value) {
                            this.setAttribute(attributeKabob, '');
                        }
                        else {
                            this.removeAttribute(attributeKabob);
                        }
                        this.queueRender();
                    }
                }
                else if (typeof attributes[attributeName] === 'number') {
                    if (value !== parseFloat(this[attributeName])) {
                        this.setAttribute(attributeKabob, value);
                        this.queueRender();
                    }
                }
                else {
                    if (typeof value === 'object' ||
                        `${value}` !== `${this[attributeName]}`) {
                        if (value === null ||
                            value === undefined ||
                            typeof value === 'object') {
                            this.removeAttribute(attributeKabob);
                        }
                        else {
                            this.setAttribute(attributeKabob, value);
                        }
                        this.queueRender();
                        attributeValues[attributeName] = value;
                    }
                }
            },
        });
    });
}
  initValue() {
    const valueDescriptor = Object.getOwnPropertyDescriptor(this, 'value');
    if (valueDescriptor === undefined ||
        valueDescriptor.get !== undefined ||
        valueDescriptor.set !== undefined) {
        return;
    }
    let value = this.hasAttribute('value')
        ? this.getAttribute('value')
        : deepClone(this.value);
    delete this.value;
    Object.defineProperty(this, 'value', {
        enumerable: false,
        get() {
            return value;
        },
        set(newValue) {
            if (value !== newValue) {
                value = newValue;
                this._valueChanged = true;
                this.queueRender(true);
            }
        },
    });
}
  _parts
  get parts() {
    const root = this.shadowRoot != null ? this.shadowRoot : this;
    if (this._parts == null) {
        this._parts = new Proxy({}, {
            get(target, ref) {

                if (typeof ref !== 'string')
                    return undefined;
                if (target[ref] === undefined) {
                    let element = root.querySelector(`[part="${ref}"]`);
                    if (element == null) {

                        element = root.querySelector(`[data-ref="${ref}"]`);
                    }
                    if (element == null) {
                        element = root.querySelector(ref);
                    }
                    if (element == null)
                        throw new Error(`elementRef "${ref}" does not exist!`);
                    element.removeAttribute('data-ref');
                    target[ref] = element;
                }
                return target[ref];
            },
        });
    }
    return this._parts;
}
  attributeChangedCallback(name = '',_oldValue = '' | null,_newValue = '' | null) {
                                                              
    const propName = kabobToCamel(name);

    if (_newValue === null && this._attrValues?.has(propName)) {
        this._attrValues.delete(propName);
    }
                                                                                         
    if (!this._legacyTrackedAttrs?.has(propName)) {
        this.queueRender(false);
    }
}
  constructor() {
    super();
    instanceCount += 1;

    this._installAttributeQueue();

    if (this.constructor.formAssociated &&
        typeof this.attachInternals === 'function' &&
        !this.internals) {
        this.internals = this.attachInternals();
    }
                                                           
    const initAttrs = this.constructor.initAttributes;
    if (initAttrs) {
        this._setupAttributeAccessors(initAttrs);
    }
    this.instanceId = `${this.tagName.toLocaleLowerCase()}-${instanceCount}`;
    this._value = deepClone(this.defaultValue);
    this._warnOnHandlerCollisions();
}
  _warnOnHandlerCollisions() {
    const ctor = this.constructor;
    if (handlerCollisionChecked.has(ctor))
        return;
    handlerCollisionChecked.add(ctor);
    queueMicrotask(() => {
        const names = new Set();
        for (const key of Object.keys(this)) {
            if (/^on[A-Z]/.test(key))
                names.add(key);
        }
        let proto = Object.getPrototypeOf(this);
        while (proto && proto !== Component.prototype) {
            for (const key of Object.getOwnPropertyNames(proto)) {
                if (/^on[A-Z]/.test(key))
                    names.add(key);
            }
            proto = Object.getPrototypeOf(proto);
        }
        const tag = this.tagName.toLowerCase();

        const usesLegacyOnResize = names.delete('onResize') && this.handleResize === undefined;
        if (usesLegacyOnResize) {
            console.warn(`<${tag}> defines 'onResize', which is deprecated. Rename it to ` +
                `'handleResize'. The on<Event> prefix is reserved for event-handler ` +
                `sugar in the elements factory (creator({ onResize }) would attach a ` +
                `'resize' listener) and is being retired for component callbacks in ` +
                `favor of the handle<Event> convention.`);
        }
        if (names.size === 0)
            return;
        const list = Array.from(names, (n) => `'${n}'`).join(', ');
        console.warn(`<${tag}> defines ${list}. The elements factory treats on<Event> property ` +
            `names as event-handler sugar — e.g. creator({ onClick }) attaches a ` +
            `'click' listener rather than assigning the property — so these members ` +
            `are shadowed and cannot be set or read via the element creator. Rename ` +
            `by intent: use 'handle<Event>' for a handler function the component ` +
            `invokes (e.g. 'handleClick'), or 'add<Event>Listener' for a method that ` +
            `registers listeners for a synthetic event the component dispatches ` +
            `(e.g. 'addClickListener').`);
    });
}
  _installAttributeQueue() {

    const initAttrs = this.constructor.initAttributes;
    if (!initAttrs)
        return;
    const guarded = new Set(Object.keys(initAttrs).map(camelToKabob));
    const queue = [];
    this._pendingAttrOps = queue;
    const proto = HTMLElement.prototype;
    this.setAttribute = (name, value) => {
        if (guarded.has(name)) {
            queue.push(['set', name, String(value)]);
        }
        else {
            proto.setAttribute.call(this, name, value);
        }
    };
    this.removeAttribute = (name) => {
        if (guarded.has(name)) {
            queue.push(['remove', name]);
        }
        else {
            proto.removeAttribute.call(this, name);
        }
    };
    queueMicrotask(() => this._drainPendingAttrOps());
}
  _drainPendingAttrOps() {
    const queue = this._pendingAttrOps;
    if (queue === undefined)
        return;
    this._pendingAttrOps = undefined;
    delete this.setAttribute;
    delete this.removeAttribute;

    const preExisting = new Set();
    for (const op of queue) {
        if (this.hasAttribute(op[1]))
            preExisting.add(op[1]);
    }
    for (const op of queue) {
        if (op[0] === 'set') {
            if (!preExisting.has(op[1]))
                this.setAttribute(op[1], op[2]);
        }
        else {

            preExisting.delete(op[1]);
            this.removeAttribute(op[1]);
        }
    }
}
  _installedAttrAccessors
  _setupAttributeAccessors(initAttrs = {}) {
    if (!this._attrValues) {
        this._attrValues = new Map();
    }
    if (!this._installedAttrAccessors) {
        this._installedAttrAccessors = new Set();
    }
    for (const attrName of Object.keys(initAttrs)) {
        const attrKabob = camelToKabob(attrName);
        const defaultValue = initAttrs[attrName];
                                                                                            
        if (attrName === 'value') {
            console.warn(`${this.tagName}: 'value' cannot be an attribute. Use the Component value property instead.`);
            continue;
        }
                                                                                   
        if (typeof defaultValue === 'object' && defaultValue !== null) {
            console.warn(`${this.tagName}: initAttributes.${attrName} is an object. Use a regular property instead.`);
            continue;
        }

        if (defaultValue === true) {
            throw new Error(`${this.tagName}: static initAttributes.${attrName} defaults a boolean attribute to true, ` +
                `but HTML boolean attributes are false-by-default (presence = true, absence = false) and ` +
                `cannot reflect a true default — it would silently become false. Use { ${attrName}: false }, ` +
                `or model it as a string/number attribute or a plain (non-attribute) property.`);
        }

        let proto = this;
        let isNonConfigurable = false;
        while (proto) {
            const descriptor = Object.getOwnPropertyDescriptor(proto, attrName);
            if (descriptor) {
                if (!descriptor.configurable || descriptor.get || descriptor.set) {
                    isNonConfigurable = true;
                    break;
                }
                break;
            }
            proto = Object.getPrototypeOf(proto);
        }
        if (isNonConfigurable) {
            continue;
        }
        this._installAttrAccessor(attrName, attrKabob, defaultValue);
    }
}
  _installAttrAccessor(attrName = '',attrKabob = '', defaultValue) {
    Object.defineProperty(this, attrName, {
        enumerable: false,

        configurable: true,
        get: () => {

            if (typeof defaultValue === 'boolean') {
                if (this.hasAttribute(attrKabob))
                    return true;
                if (this._attrValues.has(attrName))
                    return this._attrValues.get(attrName);
                return false;
            }
            else if (this.hasAttribute(attrKabob)) {
                return typeof defaultValue === 'number'
                    ? parseFloat(this.getAttribute(attrKabob))
                    : this.getAttribute(attrKabob);
            }
            else if (this._attrValues.has(attrName)) {
                return this._attrValues.get(attrName);
            }
            else {
                return defaultValue;
            }
        },
        set: (value) => {
            if (typeof defaultValue === 'boolean') {
                if (value !== this[attrName]) {
                    if (value) {
                        this.setAttribute(attrKabob, '');
                    }
                    else {
                        this.removeAttribute(attrKabob);
                    }
                    this.queueRender();
                    this._attrValues.set(attrName, !!value);
                }
            }
            else if (typeof defaultValue === 'number') {
                if (value !== parseFloat(this[attrName])) {
                    this.setAttribute(attrKabob, value);
                    this.queueRender();
                    this._attrValues.set(attrName, value);
                }
            }
            else {
                if (typeof value === 'object' ||
                    `${value}` !== `${this[attrName]}`) {
                    if (value === null ||
                        value === undefined ||
                        typeof value === 'object') {
                        this.removeAttribute(attrKabob);
                    }
                    else {
                        this.setAttribute(attrKabob, value);
                    }
                    this.queueRender();
                    this._attrValues.set(attrName, value);
                }
            }
        },
    });
    this._installedAttrAccessors.add(attrName);
}
  _recoverShadowedAttrAccessors() {
    const installed = this._installedAttrAccessors;
    const initAttrs = this.constructor.initAttributes;
    if (installed == null || initAttrs == null)
        return;
    const shadowed = [];
    for (const attrName of installed) {
        const desc = Object.getOwnPropertyDescriptor(this, attrName);
        if (desc == null || desc.get != null || desc.set != null)
            continue;
        const fieldValue = desc.value;
        delete this[attrName];
        this._installAttrAccessor(attrName, camelToKabob(attrName), initAttrs[attrName]);
        this[attrName] = fieldValue;
        shadowed.push(attrName);
    }
    if (shadowed.length > 0 && !warnedFieldShadowedAttrs.has(this.tagName)) {
        warnedFieldShadowedAttrs.add(this.tagName);
        const list = shadowed.map((n) => `'${n}'`).join(', ');
        console.warn(`<${this.tagName.toLowerCase()}> declares instance field(s) ${list} ` +
            'that shadow static initAttributes accessors (class fields use ' +
            '[[Define]] semantics). The field value was adopted and the ' +
            'reactive accessor restored — delete the field declaration; ' +
            'static initAttributes already defines the property.');
    }
}
  connectedCallback() {

    this._recoverShadowedAttrAccessors();

    this._drainPendingAttrOps();
    insertGlobalStyles(this.constructor.tagName);
    this.hydrate();
    if (this.role != null)
        this.setAttribute('role', this.role);
                                                                          
    if (this.constructor.formAssociated &&
        !this.hasAttribute('tabindex')) {
        this.setAttribute('tabindex', '0');
    }

    const resizeHandler = this.handleResize ?? this.onResize;
    if (resizeHandler !== undefined) {
        resizeObserver.observe(this);
        if (this._onResize == null) {
            this._onResize = resizeHandler.bind(this);
        }
        this.addEventListener('resize', this._onResize);
    }
    if (this.value != null && this.getAttribute('value') != null) {
        this._value = this.getAttribute('value');
    }
                                                                         
    if (this.internals && this.value !== undefined) {
        this.internals.setFormValue(this.value);
        this.validateValue();
    }
    this.queueRender();
}
  disconnectedCallback() {
    resizeObserver.unobserve(this);
}
  formResetCallback() {
    if (this.value !== undefined) {
        this.value = this.defaultValue ?? '';
    }
}
  formDisabledCallback(disabled = false) {
    if (disabled) {
        this.setAttribute('disabled', '');
    }
    else {
        this.removeAttribute('disabled');
    }
}
  formStateRestoreCallback(state) {
    if (this.value !== undefined && typeof state === 'string') {
        this.value = state;
    }
}
  _changeQueued = false;
  _renderQueued = false;
  queueRender(triggerChangeEvent = false) {
    if (!this._hydrated)
        return;
    if (!this._changeQueued)
        this._changeQueued = triggerChangeEvent;
    if (!this._renderQueued) {
        this._renderQueued = true;
        requestAnimationFrame(() => {

            if (this._changeQueued) {

                dispatch(this, 'change', { bubbles: true, composed: true });
                                                                
                if (this.internals && this.value !== undefined) {
                    this.internals.setFormValue(this.value);
                }
            }
            this._changeQueued = false;
            this._renderQueued = false;
            this.render();
        });
    }
}
  _hydrated = false;
  _whenHydrated
  _resolveHydrated
  get hydrated() {
    return this._hydrated;
}
  get whenHydrated() {
    if (this._hydrated)
        return Promise.resolve();
    if (this._whenHydrated == null) {
        this._whenHydrated = new Promise((resolve) => {
            this._resolveHydrated = resolve;
        });
    }
    return this._whenHydrated;
}
  hydrate() {
    if (!this._hydrated) {
        this.initValue();
        const cloneElements = typeof this.content !== 'function';
        let _content = typeof this.content === 'function'
            ? this.content(elements)
            : this.content;
        if (Array.isArray(_content)) {
            const hostProps = {};
            _content = _content.filter((item) => {
                if (item instanceof Node ||
                    typeof item === 'string' ||
                    typeof item === 'number' ||
                    tosiPath(item)) {
                    return true;
                }
                Object.assign(hostProps, item);
                return false;
            });
            for (const key of Object.keys(hostProps)) {
                elementSet(this, key, hostProps[key]);
            }
        }
        const ctor = this.constructor;
        const shadowStyle = ctor.shadowStyleSpec ?? ctor.styleSpec;
        if (ctor.styleSpec && !ctor.shadowStyleSpec) {
            warnDeprecated('static-styleSpec', 'static styleSpec is deprecated. Use static shadowStyleSpec instead.');
        }
        let { styleNode } = ctor;
        if (shadowStyle) {
            styleNode = ctor.styleNode = elements.style(css(shadowStyle));
            delete ctor.styleNode;
        }
        if (this.styleNode) {
            console.warn(this, 'styleNode is deprecated, use static shadowStyleSpec instead');
            styleNode = this.styleNode;
        }
        if (styleNode) {
            const shadow = this.attachShadow({ mode: 'open' });
            shadow.appendChild(styleNode.cloneNode(true));
            appendContentToElement(shadow, _content, cloneElements);

            if (!warnedShadowContentBindings.has(this.tagName) &&
                shadow.querySelector(BOUND_SELECTOR) != null) {
                warnedShadowContentBindings.add(this.tagName);
                console.warn(`<${this.tagName.toLowerCase()}> has data-binding sugar in its ` +
                    'shadow-DOM content, where bindings do not operate. A shadow-DOM ' +
                    'component is bound like an <input>: bind its VALUE from outside ' +
                    '(bindings.value) and implement render() to reflect value into ' +
                    'the shadow DOM — setting value queues render() and emits change ' +
                    'automatically. on() event handlers are fine. Warned once per ' +
                    'component class.');
            }
        }
        else if (_content !== null) {
            const existingChildren = Array.from(this.childNodes);
            appendContentToElement(this, _content, cloneElements);

            this.isSlotted =
                this.querySelector('slot,tosi-slot,xin-slot') !== null;
            const slots = Array.from(this.querySelectorAll('slot'));
            if (slots.length > 0) {
                slots.forEach(TosiSlot.replaceSlot);
            }
            if (existingChildren.length > 0) {
                const slotMap = { '': this };
                Array.from(this.querySelectorAll('tosi-slot,xin-slot')).forEach((slot) => {
                    slotMap[slot.name] = slot;
                });
                existingChildren.forEach((child) => {
                    const defaultSlot = slotMap[''];
                    const destSlot = child instanceof Element ? slotMap[child.slot] : defaultSlot;
                    (destSlot !== undefined ? destSlot : defaultSlot).append(child);
                });
            }
        }
        this._hydrated = true;

        this._parts = undefined;
        this._resolveHydrated?.();
    }
}
  render() {
                                                               
    if (this._valueChanged && this.internals && this.value !== undefined) {
        this.internals.setFormValue(this.value);
        this.validateValue();
    }
    this._valueChanged = false;
}
  validateValue() {
    if (!this.internals || this.value === undefined)
        return;
    const value = typeof this.value === 'string' ? this.value : String(this.value);
    validateAgainstConstraints(this, value);
}
}

const SlotParts = Type('SlotParts', undefined, { slotty: {} })

class TosiSlot extends Component {
  static preferredTagName = 'tosi-slot';
  static initAttributes = { name: '' }
  content = null;
  static replaceSlot(slot = {}) {
    const _slot = document.createElement('tosi-slot');
    if (slot.name !== '') {
        _slot.setAttribute('name', slot.name);
    }

    while (slot.firstChild != null) {
        _slot.appendChild(slot.firstChild);
    }
    slot.replaceWith(_slot);
}
}

export const tosiSlot = TosiSlot.elementCreator();

class XinSlot extends Component {
  static preferredTagName = 'xin-slot';
  static initAttributes = { name: '' }
  content = null;
  constructor() {
    super();
    warnDeprecated('xin-slot', '<xin-slot> is deprecated. Use <tosi-slot> instead.');
}
  static replaceSlot = TosiSlot.replaceSlot;
}

export const xinSlot = XinSlot.elementCreator();